import { useQuery } from '@tanstack/react-query';
import { getSupabase } from './supabase';

export type HouseholdRecord = {
  id: string;
  region_code: string;
  size: number;
  composition: string;
  tenure: string;
  income_band: number;
};

export type RegionSummary = {
  code: string;
  name: string;
  country: string;
};

export type PersonaProfileSummary = {
  display_name: string | null;
};

export type PersonRecord = {
  id: string;
  household_id: string;
  region_code: string;
  age_band: number;
  sex: 'm' | 'f';
  income_band: number | null;
  occupation_class: string;
  economic_activity: string;
  attributes: Record<string, unknown>;
  created_at: string;
  household: HouseholdRecord | null;
  region: RegionSummary | null;
  persona_profile: PersonaProfileSummary | null;
};

export function usePerson(id: string | null) {
  return useQuery<PersonRecord, Error>({
    queryKey: ['person', id],
    enabled: id != null,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('person')
        .select(
          'id, household_id, region_code, age_band, sex, income_band, occupation_class, economic_activity, attributes, created_at, household(*), region(code, name, country), persona_profile(display_name)'
        )
        .eq('id', id!)
        .single();
      if (error) throw new Error(error.message);
      if (!data) throw new Error('Person not found');
      const raw = data as Record<string, unknown>;
      const profileRaw = raw.persona_profile;
      const persona_profile: PersonaProfileSummary | null = Array.isArray(profileRaw)
        ? ((profileRaw[0] as PersonaProfileSummary | undefined) ?? null)
        : ((profileRaw as PersonaProfileSummary | null) ?? null);
      return { ...(raw as object), persona_profile } as PersonRecord;
    },
  });
}
